import { ServiceTypeSlug } from './ServiceType';

export enum ServiceAddOnTypeSlugs {
  additionalDog = 'additional-dog',
  bathingGrooming = 'bathing-grooming',
  catCare = 'cat-care',
  costAdjustments = 'cost-adjustments',
  dailyPickUpDropOff = 'daily-pick-up-drop-off',
  dogWalking = 'dog-walking',
  extendedCare = 'extended-care',
  extendedStay = 'extended-stay',
  holidayRate = 'holiday-rate',
  pickUpDropOff = 'pick-up-drop-off',
  puppy = 'puppy',
  shortNotice = 'short-notice',
  standardRate = 'standard-rate',
  additionalCat = 'additional-cat',
  longWalk = 'long-walk',
  longDropIn = 'long-drop-in',
  training = 'training',
  missingServiceDeliveries = 'missing-service-deliveries',
  virtualConsultation = 'virtual-consultation',
  inPersonConsultation = 'in-person-consultation',
  trainingBundlePurchase = 'training-bundle-purchase',
  trainingBundleSession = 'training-bundle-session',
}

const ServiceAddOnTypeSlugsMeta = {
  [ServiceAddOnTypeSlugs.additionalDog]: ServiceAddOnTypeSlugs.additionalDog,
  [ServiceAddOnTypeSlugs.bathingGrooming]: ServiceAddOnTypeSlugs.bathingGrooming,
  [ServiceAddOnTypeSlugs.catCare]: ServiceAddOnTypeSlugs.catCare,
  [ServiceAddOnTypeSlugs.costAdjustments]: ServiceAddOnTypeSlugs.costAdjustments,
  [ServiceAddOnTypeSlugs.dailyPickUpDropOff]: ServiceAddOnTypeSlugs.dailyPickUpDropOff,
  [ServiceAddOnTypeSlugs.dogWalking]: ServiceAddOnTypeSlugs.dogWalking,
  [ServiceAddOnTypeSlugs.extendedCare]: ServiceAddOnTypeSlugs.extendedCare,
  [ServiceAddOnTypeSlugs.extendedStay]: ServiceAddOnTypeSlugs.extendedStay,
  [ServiceAddOnTypeSlugs.holidayRate]: ServiceAddOnTypeSlugs.holidayRate,
  [ServiceAddOnTypeSlugs.pickUpDropOff]: ServiceAddOnTypeSlugs.pickUpDropOff,
  [ServiceAddOnTypeSlugs.puppy]: ServiceAddOnTypeSlugs.puppy,
  [ServiceAddOnTypeSlugs.shortNotice]: ServiceAddOnTypeSlugs.shortNotice,
  [ServiceAddOnTypeSlugs.standardRate]: ServiceAddOnTypeSlugs.standardRate,
  [ServiceAddOnTypeSlugs.additionalCat]: ServiceAddOnTypeSlugs.additionalCat,
  [ServiceAddOnTypeSlugs.longWalk]: ServiceAddOnTypeSlugs.longWalk,
  [ServiceAddOnTypeSlugs.longDropIn]: ServiceAddOnTypeSlugs.longDropIn,
  [ServiceAddOnTypeSlugs.training]: ServiceAddOnTypeSlugs.training,
  [ServiceAddOnTypeSlugs.missingServiceDeliveries]: ServiceAddOnTypeSlugs.missingServiceDeliveries,
  [ServiceAddOnTypeSlugs.virtualConsultation]: ServiceAddOnTypeSlugs.virtualConsultation,
  [ServiceAddOnTypeSlugs.inPersonConsultation]: ServiceAddOnTypeSlugs.inPersonConsultation,
  [ServiceAddOnTypeSlugs.trainingBundlePurchase]: ServiceAddOnTypeSlugs.trainingBundlePurchase,
  [ServiceAddOnTypeSlugs.trainingBundleSession]: ServiceAddOnTypeSlugs.trainingBundleSession,
};

const AdditionalRateAddOnSlugs = [
  ServiceAddOnTypeSlugs.additionalDog,
  ServiceAddOnTypeSlugs.bathingGrooming,
  ServiceAddOnTypeSlugs.pickUpDropOff,
  ServiceAddOnTypeSlugs.additionalCat,
  ServiceAddOnTypeSlugs.longWalk,
  ServiceAddOnTypeSlugs.longDropIn,
  ServiceAddOnTypeSlugs.shortNotice,
];

export const GroomingBathAddOnTypeSlugs = Object.freeze({
  smallBath: 'small-bath',
  mediumBath: 'medium-bath',
  largeBath: 'large-bath',
  giantBath: 'giant-bath',
  catBath: 'cat-bath',
});

export const GroomingHaircutAddOnTypeSlugs = Object.freeze({
  smallGroom: 'small-haircut',
  mediumGroom: 'medium-haircut',
  largeGroom: 'large-haircut',
  giantGroom: 'giant-haircut',
  catGroom: 'cat-haircut',
});

export const GroomingServiceAddOnTypeSlugs = Object.freeze({
  ...GroomingBathAddOnTypeSlugs,
  ...GroomingHaircutAddOnTypeSlugs,
  advancedCoat: 'advanced-coat',
});

// Add-ons allowed for cat-only services/brands on the sitter profile page.
// Note: catCare is excluded because it becomes the standard rate for cat-only.
export const CAT_ONLY_ALLOWED_ADD_ONS = [
  ServiceAddOnTypeSlugs.additionalCat,
  ServiceAddOnTypeSlugs.holidayRate,
  ServiceAddOnTypeSlugs.extendedCare,
  ServiceAddOnTypeSlugs.extendedStay,
  ServiceAddOnTypeSlugs.bathingGrooming,
  ServiceAddOnTypeSlugs.pickUpDropOff,
  ServiceAddOnTypeSlugs.dailyPickUpDropOff,
  ServiceAddOnTypeSlugs.longDropIn,
  ServiceAddOnTypeSlugs.shortNotice,
];

// Allowed additional-rate add-ons per service type, used on both provider and public sitter profile pages.
export const ALLOWED_ADDITIONAL_RATES_FOR_SERVICE_TYPE = new Map<string, readonly string[]>([
  [
    ServiceTypeSlug.OVERNIGHT_TRAVELING,
    [
      ServiceAddOnTypeSlugs.holidayRate,
      ServiceAddOnTypeSlugs.additionalDog,
      ServiceAddOnTypeSlugs.puppy,
      ServiceAddOnTypeSlugs.additionalCat,
      ServiceAddOnTypeSlugs.extendedStay,
      ServiceAddOnTypeSlugs.shortNotice,
    ],
  ],
  [
    ServiceTypeSlug.OVERNIGHT_BOARDING,
    [
      ServiceAddOnTypeSlugs.holidayRate,
      ServiceAddOnTypeSlugs.additionalDog,
      ServiceAddOnTypeSlugs.puppy,
      ServiceAddOnTypeSlugs.additionalCat,
      ServiceAddOnTypeSlugs.extendedStay,
      ServiceAddOnTypeSlugs.bathingGrooming,
      ServiceAddOnTypeSlugs.pickUpDropOff,
      ServiceAddOnTypeSlugs.dailyPickUpDropOff,
      ServiceAddOnTypeSlugs.shortNotice,
    ],
  ],
  [
    ServiceTypeSlug.DROP_IN,
    [
      ServiceAddOnTypeSlugs.longDropIn,
      ServiceAddOnTypeSlugs.holidayRate,
      ServiceAddOnTypeSlugs.additionalDog,
      ServiceAddOnTypeSlugs.puppy,
      ServiceAddOnTypeSlugs.additionalCat,
      ServiceAddOnTypeSlugs.extendedStay,
      ServiceAddOnTypeSlugs.bathingGrooming,
      ServiceAddOnTypeSlugs.shortNotice,
    ],
  ],
  [
    ServiceTypeSlug.DOGGY_DAY_CARE,
    [
      ServiceAddOnTypeSlugs.holidayRate,
      ServiceAddOnTypeSlugs.additionalDog,
      ServiceAddOnTypeSlugs.puppy,
      ServiceAddOnTypeSlugs.bathingGrooming,
      ServiceAddOnTypeSlugs.pickUpDropOff,
      ServiceAddOnTypeSlugs.dailyPickUpDropOff,
      ServiceAddOnTypeSlugs.shortNotice,
    ],
  ],
  [
    ServiceTypeSlug.DOG_WALKING,
    [
      ServiceAddOnTypeSlugs.longWalk,
      ServiceAddOnTypeSlugs.holidayRate,
      ServiceAddOnTypeSlugs.additionalDog,
      ServiceAddOnTypeSlugs.puppy,
      ServiceAddOnTypeSlugs.shortNotice,
    ],
  ],
]);

export const isAdditionalRateAddOn = (slug: ServiceAddOnTypeSlug): boolean =>
  AdditionalRateAddOnSlugs.includes(slug);
export type ServiceAddOnTypeSlug = keyof typeof ServiceAddOnTypeSlugsMeta;

export type ServiceAddOn = {
  active: boolean;
  duration: string | null | undefined;
  hasDuration: boolean;
  isAlternativeRate: boolean;
  isFree: boolean;
  keepRate: string;
  name: string;
  pricePerUnit: string | null | undefined;
  fullPricePerUnit: string | null | undefined;
  providerPaymentPerUnit: string | null | undefined;
  slug: ServiceAddOnTypeSlug;
  sublabel?: string | null;
  unit: string;
  unitLabel: string;
  minPrice: string;
  maxPrice: string;
  shouldShowOfferForFreeCheckbox: boolean;
  sitterHelpText: string;
  useProviderPayment: boolean;
};

export type ServiceAddOnType = {
  addonActive: boolean;
  name: string;
  slug: ServiceAddOnTypeSlug;
  sublabel?: string | null;
  unit: string;
  unitLabel: string;
  isAlternativeRate: boolean;
  hasDistance: boolean;
  hasDuration: boolean;
  shouldShowOfferForFreeCheckbox: boolean;
};
